// Mirrors the non-call WS frames the call engine (J8) forwards to the host —
// `presence.update`, `friend.request`, `friend.accepted` (docs/02 §4) — into
// state screens can subscribe to. The engine owns the socket; App.tsx wiring
// (owned by the orchestrator, see this job's final report) feeds frames here
// via `CallEngineDeps.onFrame`.

import type { PresenceStatus, WsFrame } from '@merkury/protocol';
import { Store, type ReadableStore } from '../call/store.js';

export interface LiveEventsState {
  /** userId -> last known presence tier, most recent `presence.update` wins. */
  presence: Record<string, PresenceStatus>;
  /** Bumped on every `friend.request` / `friend.accepted` — screens refetch. */
  friendEventSeq: number;
}

const INITIAL: LiveEventsState = { presence: {}, friendEventSeq: 0 };

/**
 * Resolve a user's presence tier: the live WS feed wins, then the REST
 * friends-list snapshot (`presence`, or the legacy `online` boolean).
 */
export function presenceOf(
  state: LiveEventsState,
  userId: string,
  fallback?: { presence?: PresenceStatus | undefined; online?: boolean | undefined },
): PresenceStatus {
  return (
    state.presence[userId] ?? fallback?.presence ?? (fallback?.online ? 'app' : 'offline')
  );
}

export class LiveEvents {
  private readonly store = new Store<LiveEventsState>(INITIAL);

  get state(): ReadableStore<LiveEventsState> {
    return this.store;
  }

  /** Feed one frame from `CallEngineDeps.onFrame`; ignores frames it doesn't own. */
  handleFrame(frame: WsFrame): void {
    switch (frame.type) {
      case 'presence.update': {
        // Older servers send only the boolean; fold it into the tri-state.
        const status = frame.payload.status ?? (frame.payload.online ? 'app' : 'offline');
        this.store.patch({
          presence: { ...this.store.get().presence, [frame.payload.userId]: status },
        });
        return;
      }
      case 'friend.request':
      case 'friend.accepted':
        this.store.patch({ friendEventSeq: this.store.get().friendEventSeq + 1 });
        return;
      default:
        return;
    }
  }
}
